// Heatmap viewer component
// Overlays YouCam API heatmap PNG on user's photo with opacity control

import { useState } from 'react'
import { motion } from 'framer-motion'

interface HeatmapViewerProps {
  photoUrl: string           // User's original photo
  heatmapUrl?: string        // API-provided PNG heatmap
  metricName?: string        // For display
  showControls?: boolean     // Show opacity slider
  className?: string
}

export function HeatmapViewer({
  photoUrl,
  heatmapUrl,
  metricName,
  showControls = true,
  className = '',
}: HeatmapViewerProps) {
  const [opacity, setOpacity] = useState(0.6)
  const [isHeatmapVisible, setIsHeatmapVisible] = useState(true)

  return (
    <div className={`relative ${className}`}>
      {/* Photo container with aspect ratio */}
      <div className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden bg-gray-100">
        {/* Base photo */}
        <img
          src={photoUrl}
          alt="Your skin analysis"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Heatmap overlay */}
        {heatmapUrl && isHeatmapVisible && (
          <motion.img
            src={heatmapUrl}
            alt={`${metricName || 'Metric'} heatmap`}
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              opacity,
              mixBlendMode: 'multiply',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity }}
            transition={{ duration: 0.3 }}
            onError={(e) => {
              // Hide heatmap if image fails to load
              e.currentTarget.style.display = 'none'
            }}
          />
        )}

        {/* Metric name badge */}
        {metricName && (
          <div className="absolute top-3 left-3 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full">
            <span className="text-white text-sm font-medium">{metricName}</span>
          </div>
        )}

        {/* Toggle heatmap button */}
        {heatmapUrl && (
          <button
            onClick={() => setIsHeatmapVisible(!isHeatmapVisible)}
            className="absolute top-3 right-3 p-2 bg-black/60 backdrop-blur-sm rounded-full"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {isHeatmapVisible ? (
                // Eye icon
                <>
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </>
              ) : (
                // Eye-off icon
                <>
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </>
              )}
            </svg>
          </button>
        )}
      </div>

      {/* Opacity control */}
      {showControls && heatmapUrl && isHeatmapVisible && (
        <div className="mt-3 px-2">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>Heatmap Intensity</span>
            <span>{Math.round(opacity * 100)}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={opacity * 100}
            onChange={(e) => setOpacity(Number(e.target.value) / 100)}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-500"
          />
        </div>
      )}
    </div>
  )
}

// Compact thumbnail version
interface HeatmapThumbnailProps {
  photoUrl: string
  heatmapUrl?: string
  onClick?: () => void
  className?: string
}

export function HeatmapThumbnail({ photoUrl, heatmapUrl, onClick, className = '' }: HeatmapThumbnailProps) {
  return (
    <button
      onClick={onClick}
      className={`relative rounded-xl overflow-hidden bg-gray-100 aspect-square ${className}`}
    >
      {/* Base photo */}
      <img src={photoUrl} alt="Skin" className="absolute inset-0 w-full h-full object-cover" />

      {/* Heatmap overlay */}
      {heatmapUrl && (
        <img
          src={heatmapUrl}
          alt="Heatmap"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.5, mixBlendMode: 'multiply' }}
          onError={(e) => {
            e.currentTarget.style.display = 'none'
          }}
        />
      )}

      {/* Tap indicator */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/10 transition-colors">
        <div className="w-8 h-8 rounded-full bg-white/80 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-9 9M3 21l9-9" />
          </svg>
        </div>
      </div>
    </button>
  )
}
